import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import NotificationBell from "@/components/NotificationBell";
import BillingModal from "@/components/BillingModal";
import { trialBannerCopy } from "@/lib/subscription";
import { LogOut, LayoutDashboard, Building2, Users, Wallet, FileBarChart, MapPin, ListChecks, ClipboardList, Send, FileText, CalendarCheck, Layers, AlertTriangle, Sparkles, CreditCard, Package, UserPlus, Settings, Network, Gauge } from "lucide-react";

// `module` key (matches plan.modules) tells the layout which feature this nav item belongs to.
const NAV = {
  platform_admin: [
    { to: "/platform", label: "Overview", icon: LayoutDashboard },
    { to: "/platform/companies", label: "Tenants", icon: Building2 },
    { to: "/platform/plans", label: "Plans", icon: Layers },
    { to: "/platform/gateway", label: "Gateway", icon: CreditCard },
  ],
  company_admin: [
    { to: "/company", label: "Overview", icon: LayoutDashboard },
    { to: "/company/outlets", label: "Outlets", icon: MapPin, module: "multi_outlet" },
    { to: "/company/employees", label: "Employees", icon: Users },
    { to: "/company/attendance", label: "Attendance", icon: CalendarCheck, module: "attendance" },
    { to: "/company/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
    { to: "/company/requests", label: "Requests", icon: ClipboardList },
    { to: "/company/tasks", label: "Tasks", icon: ListChecks, module: "tasks" },
    { to: "/company/assets", label: "Assets", icon: Package, module: "assets" },
    { to: "/company/referrals", label: "Referrals", icon: UserPlus, module: "referrals" },
    { to: "/company/performance", label: "Performance", icon: Gauge, module: "performance" },
    { to: "/company/reports", label: "Reports", icon: FileBarChart, module: "reports" },
    { to: "/company/settings", label: "Settings", icon: Settings },
  ],
  hr: [
    { to: "/portal", label: "Punch", icon: LayoutDashboard },
    { to: "/portal/report", label: "My Report", icon: FileText },
    { to: "/company/employees", label: "Employees", icon: Users },
    { to: "/company/attendance", label: "Attendance", icon: CalendarCheck, module: "attendance" },
    { to: "/company/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
    { to: "/company/requests", label: "Requests", icon: ClipboardList },
    { to: "/company/tasks", label: "Tasks", icon: ListChecks, module: "tasks" },
    { to: "/company/assets", label: "Assets", icon: Package, module: "assets" },
    { to: "/company/referrals", label: "Referrals", icon: UserPlus, module: "referrals" },
    { to: "/company/performance", label: "Performance", icon: Gauge, module: "performance" },
    { to: "/company/reports", label: "Reports", icon: FileBarChart, module: "reports" },
    { to: "/company/settings", label: "Settings", icon: Settings },
  ],
  accountant: [
    { to: "/portal", label: "Punch", icon: LayoutDashboard },
    { to: "/portal/report", label: "My Report", icon: FileText },
    { to: "/company/employees", label: "Employees", icon: Users },
    { to: "/company/attendance", label: "Attendance", icon: CalendarCheck, module: "attendance" },
    { to: "/company/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
    { to: "/company/assets", label: "Assets", icon: Package, module: "assets" },
    { to: "/company/referrals", label: "Referrals", icon: UserPlus, module: "referrals" },
    { to: "/company/reports", label: "Reports", icon: FileBarChart, module: "reports" },
  ],
  cluster_manager: [
    { to: "/portal", label: "Punch", icon: LayoutDashboard },
    { to: "/portal/report", label: "My Report", icon: FileText },
    { to: "/company/performance", label: "Performance", icon: Gauge, module: "performance" },
    { to: "/portal/org", label: "My Team", icon: Network },
    { to: "/portal/tasks", label: "Tasks", icon: ListChecks, module: "tasks" },
    { to: "/portal/assets", label: "Assets", icon: Package, module: "assets" },
    { to: "/company/requests", label: "Approvals", icon: ClipboardList },
  ],
  manager: [
    { to: "/portal", label: "Punch", icon: LayoutDashboard },
    { to: "/portal/report", label: "My Report", icon: FileText },
    { to: "/company/performance", label: "Performance", icon: Gauge, module: "performance" },
    { to: "/portal/org", label: "My Team", icon: Network },
    { to: "/portal/tasks", label: "Tasks", icon: ListChecks, module: "tasks" },
    { to: "/portal/assets", label: "Assets", icon: Package, module: "assets" },
    { to: "/portal/referrals", label: "Refer", icon: UserPlus, module: "referrals" },
    { to: "/company/requests", label: "Approvals", icon: ClipboardList },
  ],
  employee: [
    { to: "/portal", label: "Punch", icon: LayoutDashboard },
    { to: "/portal/report", label: "My Report", icon: FileText },
    { to: "/portal/org", label: "My Team", icon: Network },
    { to: "/portal/performance", label: "Performance", icon: Gauge, module: "performance" },
    { to: "/portal/tasks", label: "My Tasks", icon: ListChecks, module: "tasks" },
    { to: "/portal/assets", label: "My Assets", icon: Package, module: "assets" },
    { to: "/portal/referrals", label: "Refer", icon: UserPlus, module: "referrals" },
    { to: "/portal/requests", label: "Requests", icon: Send },
  ],
};

// Company-area routes gated by the per-tenant role permission matrix (self-service
// /portal/* pages are NOT gated). none => hidden.
const PERM_BY_ROUTE = {
  "/company/outlets": "outlets", "/company/employees": "employees",
  "/company/attendance": "attendance", "/company/payroll": "payroll",
  "/company/requests": "requests", "/company/tasks": "tasks",
  "/company/assets": "assets", "/company/referrals": "referrals",
  "/company/performance": "performance", "/company/reports": "reports",
  "/company/settings": "settings",
};

function TrialBanner({ sub, onOpenBilling }) {
  const copy = trialBannerCopy(sub);
  if (!copy) return null;
  const palette = {
    info: "bg-blue-50 border-blue-200 text-blue-950",
    warning: "bg-amber-50 border-amber-200 text-amber-950",
    danger: "bg-red-50 border-red-200 text-red-950",
  }[copy.tone];
  return (
    <div className={`border-b ${palette}`} data-testid="trial-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 text-sm">
        {copy.tone === "info" ? <Sparkles className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
        <div className="flex-1">
          <span className="font-semibold">{copy.headline}</span>
          <span className="hidden sm:inline ml-2 opacity-80">{copy.body}</span>
        </div>
        <button onClick={onOpenBilling} className="text-xs uppercase tracking-widest underline underline-offset-4" data-testid="trial-banner-cta">View plans</button>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, subscription } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [billingOpen, setBillingOpen] = useState(false);
  const [perms, setPerms] = useState(null);
  useEffect(() => {
    api.get("/me/permissions").then((r) => setPerms(r.data.perms)).catch(() => {});
  }, []);
  const rawItems = NAV[user?.role] || [];
  const mods = subscription?.plan?.modules || null;
  // Hide nav items whose module is disabled. Platform admin / no-subscription tenants see everything.
  const items = rawItems.filter((it) => {
    if (it.module && mods && !mods[it.module]) return false;
    const permMod = PERM_BY_ROUTE[it.to];
    if (permMod && perms && perms[permMod] === "none") return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brand size="sm" testid="brand-link" />
            <span className="hidden sm:inline text-xs text-slate-500 ml-1 uppercase tracking-widest">{user?.role?.replace("_", " ")}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-600" data-testid="user-name">{user?.name}</span>
            <NotificationBell />
            <ChangePasswordDialog />
            <Button data-testid="logout-btn" variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <TrialBanner sub={subscription} onOpenBilling={() => setBillingOpen(true)} />
      <BillingModal open={billingOpen} onOpenChange={setBillingOpen} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
            {items.map((it) => {
              const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to + "/"));
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm rounded-sm whitespace-nowrap border ${active ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                >
                  <it.icon className="w-4 h-4" strokeWidth={1.5} /> {it.label}
                </Link>
              );
            })}
          </nav>
        </aside>
        <main className="col-span-12 md:col-span-9 lg:col-span-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
